import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { PLATFORM_ROLES, PLATFORM_ROLE_LABEL, platformAdminInput, platformSettingsInput, type PlatformAdminRow, type PlatformRole, type PlatformSettings } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { PageHeader } from '@/components/shared/page-header';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { api, errorMessage } from '@/lib/api';
import { usePlans } from './orgs-page';
import { Card, dd, usePlatformAbility } from './shared';

function PolicyForm({ initial, canEdit }: { initial: PlatformSettings; canEdit: boolean }) {
  const qc = useQueryClient();
  const plans = usePlans();
  const form = useForm<z.input<typeof platformSettingsInput>>({ resolver: zodResolver(platformSettingsInput), defaultValues: initial });
  useEffect(() => form.reset(initial), [initial]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: (v: unknown) => api.put('/platform/settings', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast.success('Configurações salvas');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const e = form.formState.errors;
  const num = (name: 'graceDays' | 'pastDueDays' | 'retentionDays' | 'deletionWindowDays', label: string, hint: string) => (
    <Field id={`s-${name}`} label={label} hint={hint} error={e[name]?.message}>
      <Input id={`s-${name}`} type="number" min={0} disabled={!canEdit} {...form.register(name, { valueAsNumber: true })} />
    </Field>
  );
  return (
    <Card title="Cobrança e retenção" description="Política aplicada a todas as barbearias. Suspender nunca apaga dados.">
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {num('pastDueDays', 'Dias em atraso até a carência', 'Após esse prazo a assinatura entra em carência.')}
          {num('graceDays', 'Dias de carência', 'Depois da carência, a organização é suspensa.')}
          {num('retentionDays', 'Retenção após cancelamento (dias)', 'Prazo mínimo antes de poder excluir dados.')}
          {num('deletionWindowDays', 'Janela de exclusão (dias)', 'Tempo entre agendar e apagar de fato.')}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="s-policy" label="Quando a carência termina">
            <Select value={form.watch('suspendPolicy')} onValueChange={(v) => form.setValue('suspendPolicy', v as 'suspend' | 'notify_only')} disabled={!canEdit}>
              <SelectTrigger id="s-policy" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="suspend">Suspender a organização</SelectItem>
                <SelectItem value="notify_only">Apenas avisar (sem suspender)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field id="s-plan" label="Plano padrão dos novos cadastros">
            <Select value={form.watch('defaultPlanId') ?? '_none'} onValueChange={(v) => form.setValue('defaultPlanId', v === '_none' ? null : v)} disabled={!canEdit}>
              <SelectTrigger id="s-plan" className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Primeiro plano ativo</SelectItem>
                {(plans.data ?? []).filter((p) => p.active).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={form.watch('mrrIncludesPastDue')} onCheckedChange={(c) => form.setValue('mrrIncludesPastDue', c)} disabled={!canEdit} aria-label="Incluir atrasadas no MRR" />
          Incluir assinaturas atrasadas no cálculo do MRR
        </label>
        {canEdit && <Button type="submit" className="justify-self-start" disabled={save.isPending}>Salvar configurações</Button>}
      </form>
    </Card>
  );
}

function AddAdminModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const form = useForm<z.input<typeof platformAdminInput>>({ resolver: zodResolver(platformAdminInput), defaultValues: { role: 'support' } });
  const add = useMutation({
    mutationFn: (v: unknown) => api.post('/platform/admins', v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'admins'] });
      toast.success('Administrador adicionado');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <FormModal
      open
      onOpenChange={(o) => !o && onClose()}
      size="sm"
      title="Adicionar administrador"
      description="A pessoa precisa já ter uma conta no sistema."
      footer={<><Button variant="outline" onClick={onClose}>Cancelar</Button><Button type="submit" form="add-admin" disabled={add.isPending}>Adicionar</Button></>}
    >
      <form id="add-admin" noValidate className="grid gap-4" onSubmit={form.handleSubmit((v) => add.mutate(v))}>
        <Field id="ad-email" label="E-mail" error={form.formState.errors.email?.message}><Input id="ad-email" type="email" {...form.register('email')} /></Field>
        <Field id="ad-role" label="Papel">
          <Select value={form.watch('role')} onValueChange={(v) => form.setValue('role', v as PlatformRole)}>
            <SelectTrigger id="ad-role" className="w-full"><SelectValue /></SelectTrigger>
            <SelectContent>{PLATFORM_ROLES.map((r) => <SelectItem key={r} value={r}>{PLATFORM_ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </form>
    </FormModal>
  );
}

function AdminsCard({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const q = useQuery({ queryKey: ['platform', 'admins'], queryFn: () => api.get<PlatformAdminRow[]>('/platform/admins') });
  const refresh = () => qc.invalidateQueries({ queryKey: ['platform', 'admins'] });
  const setRole = useMutation({ mutationFn: ({ id, role }: { id: string; role: PlatformRole }) => api.patch(`/platform/admins/${id}`, { role }), onSuccess: refresh, onError: (e) => toast.error(errorMessage(e)) });
  const remove = useMutation({ mutationFn: (id: string) => api.del(`/platform/admins/${id}`), onSuccess: refresh, onError: (e) => toast.error(errorMessage(e)) });
  return (
    <Card title="Administradores da plataforma" description="Papéis: proprietário (tudo), suporte, financeiro e somente leitura." actions={canEdit && <Button size="sm" onClick={() => setAdding(true)}><Plus className="size-4" /> Adicionar</Button>}>
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} /> : (
        <ul className="divide-y">
          {(q.data ?? []).map((a) => (
            <li key={a.userId} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
              <span className="min-w-0"><span className="font-medium">{a.name}</span><span className="block truncate text-xs text-muted-foreground">{a.email} · desde {dd(a.createdAt)}</span></span>
              <span className="flex items-center gap-2">
                <Select value={a.role} onValueChange={(r) => setRole.mutate({ id: a.userId, role: r as PlatformRole })} disabled={!canEdit}>
                  <SelectTrigger size="sm" className="w-40" aria-label={`Papel de ${a.name}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORM_ROLES.map((r) => <SelectItem key={r} value={r}>{PLATFORM_ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
                </Select>
                {canEdit && (
                  <ConfirmDialog trigger={<Button size="icon" variant="ghost" aria-label={`Remover ${a.name}`}><Trash2 className="size-4" /></Button>} title="Remover administrador?" description={`${a.name} perde o acesso à administração da plataforma. A conta continua existindo.`} confirmLabel="Remover" onConfirm={() => remove.mutateAsync(a.userId)} />
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {adding && <AddAdminModal onClose={() => setAdding(false)} />}
    </Card>
  );
}

export function PlatformSettingsPage() {
  const { isOwner } = usePlatformAbility();
  const q = useQuery({ queryKey: ['platform', 'settings'], queryFn: () => api.get<PlatformSettings>('/platform/settings') });
  return (
    <div className="grid grid-cols-1 gap-6">
      <PageHeader title="Configurações" description="Carência, retenção, exclusão e administradores da plataforma." />
      {q.isLoading ? <LoadingState /> : q.error || !q.data ? <ErrorState error={q.error} onRetry={q.refetch} /> : <PolicyForm initial={q.data} canEdit={isOwner} />}
      <AdminsCard canEdit={isOwner} />
      <Card title="Políticas">
        <ul className="grid list-disc gap-1 pl-5 text-sm text-muted-foreground">
          <li>Exportação: o proprietário da barbearia baixa os próprios dados (JSON e CSV em zip), mesmo com a conta suspensa.</li>
          <li>Retenção: depois do cancelamento, os dados ficam guardados pelo prazo configurado acima.</li>
          <li>Exclusão: duas etapas (agendar e aguardar a janela), somente pelo proprietário da plataforma, com cancelamento possível até o fim da janela.</li>
          <li>Suspensão nunca apaga dados: a barbearia segue com leitura e exportação.</li>
        </ul>
      </Card>
    </div>
  );
}
